import React from "react";
import dayjs from "dayjs";

import {
  DeviceByTypeData,
  DeviceReference,
  DevicePackDataState,
  isDeviceOnline,
} from "modules/devices";

export enum AggregatedHealthStatus {
  Healthy,
  Warning,
  Unhealthy,
}

export const getAggregatedDeviceStatus = ({
  deviceData,
  configuredDevices,
  getNow,
}: {
  configuredDevices: DeviceReference[];
  deviceData: DeviceByTypeData[];
  getNow: () => number;
}) => {
  const onlineDevices = deviceData.filter((x) =>
    isDeviceOnline(dayjs.utc(x.timestamp_utc), getNow())
  );

  const offlineDeviceCount = configuredDevices.length - onlineDevices.length;
  const onlineDevicesWithFault = onlineDevices.filter(
    (x) => x.state === DevicePackDataState.Fault
  );
  const onlineDevicesWithoutFault = onlineDevices.filter(
    (x) => x.state !== DevicePackDataState.Fault
  );

  const alerts = [] as [string, AggregatedHealthStatus][];

  const buildMessage = (affectedCount: number, state: string) => {
    if (affectedCount < 1) return ``;
    if (configuredDevices.length === 1) {
      return `Device is ${state}`;
    }
    if (affectedCount === configuredDevices.length)
      return `All devices are ${state}`;
    if (alerts.length > 0)
      return `${affectedCount} ${
        affectedCount > 1 ? "devices are" : "device is"
      } ${state}`;
    return `${affectedCount} of ${configuredDevices.length} devices are ${state}`;
  };

  if (onlineDevicesWithFault.length > 0) {
    alerts.push([
      buildMessage(onlineDevicesWithFault.length, "unhealthy due to a fault"),
      AggregatedHealthStatus.Unhealthy,
    ]);
  }

  if (offlineDeviceCount > 0) {
    // consider this unhealthy if all devices are offline
    // except if we're representing a single device, in which
    // case we just want to show it as a warning
    const healthState =
      offlineDeviceCount === configuredDevices.length &&
      configuredDevices.length > 1
        ? AggregatedHealthStatus.Unhealthy
        : AggregatedHealthStatus.Warning;
    alerts.push([buildMessage(offlineDeviceCount, "offline"), healthState]);
  }

  if (onlineDevicesWithoutFault.length > 0) {
    alerts.push([
      buildMessage(onlineDevicesWithoutFault.length, "healthy"),
      AggregatedHealthStatus.Healthy,
    ]);
  }

  const mostSevereHealthStatus = alerts.map((x) => x[1]).sort()[
    alerts.length - 1
  ];
  return {
    messages: alerts.map((x) => x[0]),
    aggregatedHealthStatus: mostSevereHealthStatus,
  };
};
export const AggregatedDeviceStatus = ({
  deviceData,
  devices: configuredDevices,
  getNow,
}: {
  devices: DeviceReference[];
  deviceData: DeviceByTypeData[];
  getNow: () => number;
}) => {
  const { aggregatedHealthStatus, messages } = getAggregatedDeviceStatus({
    configuredDevices,
    deviceData,
    getNow,
  });

  if (configuredDevices.length === 0)
    return (
      <div
        className={`rounded-full w-4 h-4 py-1 px-1 inline-block ml-2 bg-gray-400`}
        title={"Awaiting data..."}
      ></div>
    );

  return (
    <div
      className={`rounded-full w-4 h-4 py-1 px-1 inline-block ml-2 ${
        aggregatedHealthStatus === AggregatedHealthStatus.Healthy
          ? "bg-green-400"
          : aggregatedHealthStatus === AggregatedHealthStatus.Warning
          ? "bg-yellow-400"
          : "bg-red-400"
      }`}
      title={messages.join(". ")}
    ></div>
  );
};
